import { memo, useMemo } from 'react'
import { useRouter } from 'next/compat/router'
import isEqual from 'react-fast-compare'
import { useInstantSearch, useSearchBox } from 'react-instantsearch'
import { focusElementClasses, FocusElement, AtIcon } from '@curran-catalog/curran-atomic-library'

import { environment } from '@config/environment'
import { FiltersProps } from '@templates/tm-catalog'
import { SortBy, Stats } from '@utils/algolia-widgets'

const ToggleDesktopFilters = ({
  showRefinements,
  onShowRefinements,
}: {
  showRefinements: FiltersProps['showDesktopRefinements']
  onShowRefinements: FiltersProps['onShowDesktopRefinements']
}) => {
  const { uiState } = useInstantSearch()
  const { refinementList } = uiState[environment.algolia.recordsIndex]

  const allRefinementList = useMemo(
    () => (refinementList ? Object.values(refinementList).reduce((acc, value) => acc + value?.length, 0) : 0),
    [refinementList],
  )

  return (
    <button
      aria-label={`${showRefinements ? 'Hide filters' : 'Show filters'}`}
      type="button"
      className={`hidden opacity-0 md:opacity-100 md:flex ${
        focusElementClasses[FocusElement.FOCUS_VISIBLE]
      } md:py-3 md:gap-4 md:pr-2`}
      onClick={() => onShowRefinements()}
    >
      <span className="hidden sm:block font-medium text-secondary">{`${showRefinements ? 'Hide' : ''} Filter `}</span>
      {!!allRefinementList && <span className="font-medium text-secondary">{`(${allRefinementList})`}</span>}
      <span>
        <AtIcon type="filters" color="secondary" />
      </span>
    </button>
  )
}

const ToggleMobileFilters = ({
  showRefinements,
  onShowRefinements,
}: {
  showRefinements: FiltersProps['showMobileRefinements']
  onShowRefinements: FiltersProps['onShowMobileRefinements']
}) => {
  return (
    <button
      aria-label={`${showRefinements ? 'Hide filters' : 'Show filters'}`}
      type="button"
      className={`flex ${focusElementClasses[FocusElement.FOCUS_VISIBLE]} py-3 gap-4 pr-2 md:hidden md:opacity-0`}
      onClick={() => {
        document.body.style.overflow = 'hidden'
        document.body.style.touchAction = 'none'

        onShowRefinements()
      }}
    >
      <span>
        <AtIcon type="filters" color="secondary" />
      </span>
    </button>
  )
}

export const NavFilters = memo(
  ({
    showDesktopRefinements,
    showMobileRefinements,
    onShowDesktopRefinements,
    onShowMobileRefinements,
  }: FiltersProps) => {
    const router = useRouter()
    const { query } = useSearchBox()

    const hierarchyPath = router?.asPath?.split('?')[0].replace('#top', '').replace('#', '')
    const nestedPath = hierarchyPath?.split('/') ?? []
    const titleProduct = nestedPath[nestedPath?.length - 1]?.replace(/-/g, ' ').toLocaleLowerCase() ?? ''

    return (
      <>
        {query && (
          <div className="flex flex-row gap-4 items-center text-base">
            <span>
              Search results for <strong>{`"${query}"`}</strong>
            </span>
          </div>
        )}
        <div className="flex flex-col flex-shrink-0 py-4 md:pt-0 sticky md:relative z-10 bg-white transition-all top-0">
          <div className="flex items-center justify-between">
            <ToggleDesktopFilters
              showRefinements={showDesktopRefinements}
              onShowRefinements={onShowDesktopRefinements}
            />
            <div className="flex flex-row gap-1 items-center">
              <ToggleMobileFilters
                showRefinements={showMobileRefinements}
                onShowRefinements={onShowMobileRefinements}
              />
              <span className="text-sm sm:hidden font-medium text-secondary capitalize">
                {titleProduct !== 'catalog' && titleProduct}
              </span>
            </div>
            <div className="flex flex-row items-center sm:gap-4 pl-2 justify-end">
              <span className="hidden sm:block sm:text-base sm:font-medium sm:text-secondary capitalize">
                {titleProduct !== 'catalog' && titleProduct}
              </span>
              <Stats />
              <span className="hidden md:block">|</span>
              <div className="hidden opacity-0 md:opacity-100 md:flex md:items-center md:gap-4">
                <span className="hidden md:block font-medium">Sort By</span>
                <SortBy />
              </div>
            </div>
          </div>
        </div>
      </>
    )
  },
  isEqual,
)
